import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type TikTokOrderItem = {
  id: number;
  bill_no: string;
  product_code: string | null;
  product_name: string | null;
  qty: number;
  unit_price: number;
  tiktok_net_payout: number;
};

type TikTokOrder = {
  bill_no: string;
  sale_channel: string | null;
  payment_method: string | null;
  tiktok_status: string;
  tiktok_order_no: string | null;
  rec_date_be: string | null;
  rec_time: string | null;
  total_items: number;
  total_amount: number;
  discount_amount: number;
  package_fee: number;
  package_product_code: string | null;
  shipping_fee: number;
  tiktok_payout: number;
  created_at: string | null;
  order_items: TikTokOrderItem[];
};

type PackageSpecialItem = {
  id: number;
  product_code: string;
  group_key: "PACKAGE";
  sort_order: number;
  is_active: boolean;
  product: {
    code: string;
    name: string;
    price: number;
    estimated_unit_cost: number | null;
    available_qty: number;
  };
};

type TikTokOrdersResponse = {
  ok?: boolean;
  items?: unknown;
  error?: string;
};

type EditDraft = {
  tiktok_order_no: string;
  package_fee: string;
  package_product_code: string;
  shipping_fee: string;
  order_items: Record<number, string>;
};

type PayoutEditMode = "TOTAL" | "ITEMS";

const readonlyFieldStyle: React.CSSProperties = {
  backgroundColor: "#f8f9fa",
  cursor: "not-allowed",
};

const TIKTOK_ORDERS_API = apiUrl("/tiktok-shop-orders");
const SCAN_SPECIAL_ITEMS_API = apiUrl("/scan-special-items");

function toNumber(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function toTikTokOrderItem(row: unknown): TikTokOrderItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isInteger(id) || id <= 0) return null;

  return {
    id,
    bill_no: typeof data.bill_no === "string" ? data.bill_no.trim() : "",
    product_code: typeof data.product_code === "string" ? data.product_code : null,
    product_name: typeof data.product_name === "string" ? data.product_name : null,
    qty: toNumber(data.qty),
    unit_price: toNumber(data.unit_price),
    tiktok_net_payout: toNumber(data.tiktok_net_payout),
  };
}

function toTikTokOrder(row: unknown): TikTokOrder | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const billNo = typeof data.bill_no === "string" ? data.bill_no.trim() : "";
  if (!billNo) return null;

  return {
    bill_no: billNo,
    sale_channel: typeof data.sale_channel === "string" ? data.sale_channel : null,
    payment_method: typeof data.payment_method === "string" ? data.payment_method : null,
    tiktok_status:
      typeof data.tiktok_status === "string" && data.tiktok_status.trim()
        ? data.tiktok_status.trim().toUpperCase()
        : "PENDING",
    tiktok_order_no:
      typeof data.tiktok_order_no === "string" && data.tiktok_order_no.trim()
        ? data.tiktok_order_no.trim()
        : null,
    rec_date_be: typeof data.rec_date_be === "string" ? data.rec_date_be : null,
    rec_time: typeof data.rec_time === "string" ? data.rec_time : null,
    total_items: toNumber(data.total_items),
    total_amount: toNumber(data.total_amount),
    discount_amount: toNumber(data.discount_amount),
    package_fee: toNumber(data.package_fee),
    package_product_code:
      typeof data.package_product_code === "string" && data.package_product_code.trim()
        ? data.package_product_code.trim()
        : null,
    shipping_fee: toNumber(data.shipping_fee),
    tiktok_payout: toNumber(data.tiktok_payout),
    created_at: typeof data.created_at === "string" ? data.created_at : null,
    order_items: Array.isArray(data.order_items)
      ? data.order_items
          .map(toTikTokOrderItem)
          .filter((item): item is TikTokOrderItem => item != null)
      : [],
  };
}

function toPackageSpecialItem(row: unknown): PackageSpecialItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  const product_code = typeof data.product_code === "string" ? data.product_code.trim() : "";
  const group_key = typeof data.group_key === "string" ? data.group_key.trim().toUpperCase() : "";
  const product = data.product && typeof data.product === "object" ? (data.product as Record<string, unknown>) : null;
  const name = typeof product?.name === "string" ? product.name.trim() : "";
  const price = Number(product?.price ?? 0);
  const estimatedUnitCost =
    product?.estimated_unit_cost == null ? null : Number(product.estimated_unit_cost);

  if (!Number.isInteger(id) || id <= 0) return null;
  if (!product_code || group_key !== "PACKAGE" || !name || !Number.isFinite(price)) return null;

  return {
    id,
    product_code,
    group_key: "PACKAGE",
    sort_order: toNumber(data.sort_order),
    is_active: Boolean(data.is_active),
    product: {
      code: product_code,
      name,
      price,
      estimated_unit_cost: Number.isFinite(estimatedUnitCost ?? NaN) ? estimatedUnitCost : null,
      available_qty: toNumber(product?.available_qty),
    },
  };
}

function formatCurrency(value: number): string {
  return value.toLocaleString("th-TH", {
    style: "currency",
    currency: "THB",
  });
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "failed_to_fetch_tiktok_shop_orders":
      return "Unable to load TikTok Shop orders right now.";
    case "failed_to_fetch_scan_special_items":
      return "Unable to load package options right now.";
    case "bill_no_required":
      return "This order is missing a bill number.";
    case "invalid_package_fee":
      return "Package fee must be a valid number and cannot be negative.";
    case "invalid_shipping_fee":
      return "Shipping fee must be a valid number and cannot be negative.";
    case "invalid_tiktok_payout":
      return "Each TikTok net payout must be a valid number.";
    case "invalid_tiktok_order_items":
      return "One or more TikTok Shop items are invalid for this bill.";
    case "order_not_found":
      return "The selected order was not found.";
    case "failed_to_update_tiktok_fields":
      return "Unable to save TikTok Shop values right now.";
    case "package_product_not_found":
      return "The selected package product was not found.";
    case "package_stock_not_enough":
      return "Package stock is not enough for this order.";
    case "invalid_tiktok_status":
      return "TikTok status is invalid.";
    case "failed_to_update_tiktok_status":
      return "Unable to update TikTok status right now.";
    default:
      return "Something went wrong while loading TikTok Shop orders.";
  }
}

function getTikTokStatusLabel(status: string): string {
  return status === "COMPLETED" ? "เสร็จสิ้น" : "ยังไม่ครบ";
}

function getTikTokStatusBadgeClass(status: string): string {
  return status === "COMPLETED"
    ? "badge rounded-pill text-bg-success"
    : "badge rounded-pill text-bg-danger";
}

function distributeTotalTikTokPayout(
  items: TikTokOrderItem[],
  total: number
): Record<number, string> {
  // PMN-CRITICAL(/TikTokShopOrdersPage): distribute in cents so item payouts sum exactly to the user-entered TikTok total.
  if (items.length === 0) return {};

  // ทำงานใน "สตางค์" (×100) เพื่อให้ผลรวมตรงพอดีเสมอ
  const totalCents = Math.round(Math.max(0, total) * 100);
  const weightedItems = items.map((item, index) => ({
    id: item.id,
    index,
    weight: Math.max(0, toNumber(item.unit_price) * Math.max(1, toNumber(item.qty))),
  }));
  const totalWeight = weightedItems.reduce((sum, item) => sum + item.weight, 0);

  if (totalWeight <= 0) {
    const base = Math.floor(totalCents / items.length);
    let remainder = totalCents - base * items.length;
    return items.reduce<Record<number, string>>((acc, item) => {
      const cents = base + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder -= 1;
      acc[item.id] = String(cents / 100);
      return acc;
    }, {});
  }

  const provisional = weightedItems.map((item) => {
    const exact = (totalCents * item.weight) / totalWeight;
    const floorCents = Math.floor(exact);
    return { ...item, floorCents, fraction: exact - floorCents };
  });

  let remainder = totalCents - provisional.reduce((sum, item) => sum + item.floorCents, 0);

  provisional.sort((a, b) => {
    if (b.fraction !== a.fraction) return b.fraction - a.fraction;
    if (b.weight !== a.weight) return b.weight - a.weight;
    return a.index - b.index;
  });

  for (let i = 0; i < provisional.length && remainder > 0; i += 1) {
    provisional[i].floorCents += 1;
    remainder -= 1;
  }

  return provisional.reduce<Record<number, string>>((acc, item) => {
    acc[item.id] = String(item.floorCents / 100);
    return acc;
  }, {});
}

export default function TikTokShopOrdersPage(): JSX.Element {
  const [items, setItems] = useState<TikTokOrder[]>([]);
  const [packageItems, setPackageItems] = useState<PackageSpecialItem[]>([]);
  const [packageItemsLoading, setPackageItemsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedBillNos, setExpandedBillNos] = useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = useState<Record<string, EditDraft>>({});
  const [payoutModes, setPayoutModes] = useState<Record<string, PayoutEditMode>>({});
  const [totalPayoutRaw, setTotalPayoutRaw] = useState<Record<string, string>>({});
  const [savingBillNo, setSavingBillNo] = useState<string | null>(null);
  const [statusUpdatingBillNo, setStatusUpdatingBillNo] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "PENDING" | "COMPLETED">("ALL");

  useEffect(() => {
    void loadOrders();
    void loadPackageItems();
  }, []);

  async function loadOrders(): Promise<void> {
    setLoading(true);
    setError("");

    try {
      const resp = await fetch(TIKTOK_ORDERS_API);
      const data = (await resp.json().catch(() => ({}))) as TikTokOrdersResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_tiktok_shop_orders");
      }

      const nextItems = data.items
        .map(toTikTokOrder)
        .filter((item): item is TikTokOrder => item != null);

      setItems(nextItems);
      setDrafts(
        nextItems.reduce<Record<string, EditDraft>>((acc, item) => {
          acc[item.bill_no] = {
            tiktok_order_no: item.tiktok_order_no ?? "",
            package_fee: String(item.package_fee),
            package_product_code: item.package_product_code ?? "",
            shipping_fee: String(item.shipping_fee),
            order_items: item.order_items.reduce<Record<number, string>>((itemAcc, orderItem) => {
              itemAcc[orderItem.id] = String(orderItem.tiktok_net_payout);
              return itemAcc;
            }, {}),
          };
          return acc;
        }, {})
      );
      setPayoutModes(
        nextItems.reduce<Record<string, PayoutEditMode>>((acc, item) => {
          acc[item.bill_no] = "TOTAL";
          return acc;
        }, {})
      );
      setError("");
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_tiktok_shop_orders";
      setError(getErrorMessage(code));
    } finally {
      setLoading(false);
    }
  }

  async function loadPackageItems(): Promise<void> {
    setPackageItemsLoading(true);
    try {
      const token = localStorage.getItem("bg_shop_token") ?? "";
      const resp = await fetch(SCAN_SPECIAL_ITEMS_API, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok || !Array.isArray(data.items)) {
        throw new Error(data?.error ?? "failed_to_fetch_scan_special_items");
      }

      const nextItems = data.items
        .map(toPackageSpecialItem)
        .filter((item: PackageSpecialItem | null): item is PackageSpecialItem => item != null)
        .filter((item: PackageSpecialItem) => item.is_active)
        .sort((a: PackageSpecialItem, b: PackageSpecialItem) => {
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
          return a.id - b.id;
        });

      setPackageItems(nextItems);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_scan_special_items";
      setError(getErrorMessage(code));
    } finally {
      setPackageItemsLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const statusMatched =
        statusFilter === "ALL" ? true : item.tiktok_status === statusFilter;
      if (!statusMatched) return false;
      if (!q) return true;

      const haystack = [
        item.bill_no,
        item.payment_method ?? "",
        item.sale_channel ?? "",
        ...item.order_items.map((orderItem) => `${orderItem.product_code ?? ""} ${orderItem.product_name ?? ""}`),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query, statusFilter]);

  function applyTotalTikTokPayout(item: TikTokOrder, draft: EditDraft, nextTotalRaw: string): void {
    const parsed = Number(nextTotalRaw);
    const nextTotal = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    const distributed = distributeTotalTikTokPayout(item.order_items, nextTotal);

    setDrafts((prev) => ({
      ...prev,
      [item.bill_no]: {
        ...draft,
        order_items: distributed,
      },
    }));
  }

  async function handleSave(item: TikTokOrder): Promise<void> {
    // PMN-CRITICAL(/TikTokShopOrdersPage): saving can update orders, order_items payout, and real package stock allocation in backend transaction.
    const draft = drafts[item.bill_no];
    if (!draft) return;

    const tiktokOrderNo = draft.tiktok_order_no.trim();
    const packageFee = Number(draft.package_fee || 0);
    const packageProductCode = draft.package_product_code.trim();
    const shippingFee = Number(draft.shipping_fee || 0);
    const orderItemsPayload = item.order_items.map((orderItem) => ({
      id: orderItem.id,
      tiktok_net_payout: Number(draft.order_items[orderItem.id] ?? orderItem.tiktok_net_payout ?? 0),
    }));

    if (!Number.isFinite(packageFee) || packageFee < 0) {
      setError("Package fee must be a valid number and cannot be negative.");
      return;
    }
    if (!Number.isFinite(shippingFee) || shippingFee < 0) {
      setError("Shipping fee must be a valid number and cannot be negative.");
      return;
    }
    if (orderItemsPayload.some((row) => !Number.isFinite(row.tiktok_net_payout))) {
      setError("Each TikTok net payout must be a valid number.");
      return;
    }
    if (orderItemsPayload.some((row) => row.tiktok_net_payout < 0)) {
      setError("Each TikTok net payout cannot be negative.");
      return;
    }

    setSavingBillNo(item.bill_no);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(item.bill_no)}/tiktok-fields`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tiktok_order_no: tiktokOrderNo || null,
          package_fee: packageFee,
          package_product_code: packageProductCode || null,
          shipping_fee: shippingFee,
          order_items: orderItemsPayload,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(data?.error ?? "failed_to_update_tiktok_fields");
      }

      const nextTikTokPayout = orderItemsPayload.reduce(
        (sum, row) => sum + row.tiktok_net_payout,
        0
      );

      setItems((prev) =>
        prev.map((row) =>
          row.bill_no === item.bill_no
            ? {
                ...row,
                tiktok_order_no: tiktokOrderNo || null,
                package_fee: packageFee,
                package_product_code: packageProductCode || null,
                shipping_fee: shippingFee,
                tiktok_payout: nextTikTokPayout,
                order_items: row.order_items.map((orderItem) => {
                  const nextItem = orderItemsPayload.find((payloadItem) => payloadItem.id === orderItem.id);
                  return nextItem
                    ? { ...orderItem, tiktok_net_payout: nextItem.tiktok_net_payout }
                    : orderItem;
                }),
              }
            : row
        )
      );
      setSuccessMessage(`TikTok values for bill ${item.bill_no} were saved successfully.`);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_tiktok_fields";
      setError(getErrorMessage(code));
    } finally {
      setSavingBillNo(null);
    }
  }

  async function handleStatusUpdate(item: TikTokOrder, status: "PENDING" | "COMPLETED"): Promise<void> {
    // PMN-CRITICAL(/TikTokShopOrdersPage): status is manual metadata only; it does not validate that TikTok payout/package fields are complete.
    setStatusUpdatingBillNo(item.bill_no);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(item.bill_no)}/tiktok-status`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(data?.error ?? "failed_to_update_tiktok_status");
      }

      setItems((prev) =>
        prev.map((row) =>
          row.bill_no === item.bill_no
            ? {
                ...row,
                tiktok_status: status,
              }
            : row
        )
      );
      setSuccessMessage(
        status === "COMPLETED"
          ? `Bill ${item.bill_no} marked as completed.`
          : `Bill ${item.bill_no} marked as pending.`
      );
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_tiktok_status";
      setError(getErrorMessage(code));
    } finally {
      setStatusUpdatingBillNo(null);
    }
  }

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">TikTok Shop Orders</h2>
                    <div className="text-muted">
                      Show only TikTok Shop bills and fill in package fee, shipping fee, and TikTok payout later.
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => void loadOrders()}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Refresh"}
                    </button>
                    <Link to="/" className="btn btn-outline-primary">
                      Back to Menu
                    </Link>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === "ALL" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setStatusFilter("ALL")}
                    >
                      ทั้งหมด
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === "PENDING" ? "btn-danger" : "btn-outline-danger"}`}
                      onClick={() => setStatusFilter("PENDING")}
                    >
                      ยังไม่ครบ
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === "COMPLETED" ? "btn-success" : "btn-outline-success"}`}
                      onClick={() => setStatusFilter("COMPLETED")}
                    >
                      เสร็จสิ้น
                    </button>
                  </div>

                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search by bill no, payment method, channel, or product"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                {successMessage && (
                  <div className="alert alert-success rounded-3">{successMessage}</div>
                )}

                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 120 }}>Bill No</th>
                        <th style={{ width: 170 }}>Date</th>
                        <th style={{ width: 180 }}>TikTok Order No</th>
                        <th style={{ width: 120 }}>Payment</th>
                        <th style={{ width: 130 }}>Status</th>
                        <th className="text-end" style={{ width: 120 }}>Items</th>
                        <th className="text-end" style={{ width: 180 }}>Gross Sales</th>
                        <th className="text-end" style={{ width: 180 }}>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            Loading TikTok Shop orders...
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            No TikTok Shop orders found.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((item) => {
                          const isExpanded = expandedBillNos[item.bill_no] === true;
                          const draft = drafts[item.bill_no] ?? {
                            tiktok_order_no: item.tiktok_order_no ?? "",
                            package_fee: String(item.package_fee),
                            package_product_code: item.package_product_code ?? "",
                            shipping_fee: String(item.shipping_fee),
                            order_items: item.order_items.reduce<Record<number, string>>((itemAcc, orderItem) => {
                              itemAcc[orderItem.id] = String(orderItem.tiktok_net_payout);
                              return itemAcc;
                            }, {}),
                          };
                          const payoutMode = payoutModes[item.bill_no] ?? "TOTAL";
                          const totalRawKey = item.bill_no;
                          const isSaving = savingBillNo === item.bill_no;
                          const isUpdatingStatus = statusUpdatingBillNo === item.bill_no;
                          const selectedPackageItem =
                            packageItems.find(
                              (packageItem) =>
                                packageItem.product_code === draft.package_product_code
                            ) ?? null;
                          const draftOrderItems = item.order_items.map((orderItem) => ({
                            ...orderItem,
                            draftNetPayout: draft.order_items[orderItem.id] ?? String(orderItem.tiktok_net_payout),
                          }));
                          const packageFee = toNumber(draft.package_fee);
                          const shippingFee = toNumber(draft.shipping_fee);
                          const discountAmount = toNumber(item.discount_amount);
                          const totalTikTokPayout = draftOrderItems.reduce(
                            (sum, orderItem) => sum + toNumber(orderItem.draftNetPayout),
                            0
                          );
                          const tiktokFee = Math.max(0, item.total_amount - totalTikTokPayout);
                          const actualSalesReceived =
                            item.total_amount -
                            tiktokFee -
                            packageFee -
                            shippingFee -
                            discountAmount;

                          return (
                            <React.Fragment key={item.bill_no}>
                              <tr
                                className={isExpanded ? "table-primary" : undefined}
                                style={
                                  isExpanded
                                    ? {
                                        background: "linear-gradient(90deg, rgba(110,168,254,0.18) 0%, rgba(244,248,255,0.92) 18%, rgba(255,255,255,1) 100%)",
                                        borderLeft: "4px solid #6ea8fe",
                                      }
                                    : undefined
                                }
                              >
                                <td className="fw-semibold">#{item.bill_no}</td>
                                <td>{formatDateTime(item.created_at)}</td>
                                <td>{item.tiktok_order_no ?? "-"}</td>
                                <td>{item.payment_method ?? "-"}</td>
                                <td>
                                  <span className={getTikTokStatusBadgeClass(item.tiktok_status)}>
                                    {getTikTokStatusLabel(item.tiktok_status)}
                                  </span>
                                </td>
                                <td className="text-end">{item.total_items}</td>
                                <td className="text-end">{formatCurrency(item.total_amount)}</td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() =>
                                      setExpandedBillNos((prev) => ({
                                        ...prev,
                                        [item.bill_no]: !isExpanded,
                                      }))
                                    }
                                  >
                                    {isExpanded ? "Hide Details" : "View Details"}
                                  </button>
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td
                                    colSpan={8}
                                    className="bg-light"
                                    style={{
                                      borderTop: "0",
                                      borderLeft: "4px solid #6ea8fe",
                                      borderRight: "1px solid rgba(110, 168, 254, 0.12)",
                                      borderBottom: "1px solid rgba(110, 168, 254, 0.12)",
                                    }}
                                  >
                                    <div
                                      className="p-3 rounded-3"
                                      style={{
                                        background: "#fbfdff",
                                        boxShadow: "0 10px 26px rgba(13, 110, 253, 0.08)",
                                      }}
                                    >
                                      <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-4">
                                          <label className="form-label">TikTok Order No</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={draft.tiktok_order_no}
                                            placeholder="เช่น 578912345678901234"
                                            onChange={(e) =>
                                              setDrafts((prev) => ({
                                                ...prev,
                                                [item.bill_no]: {
                                                  ...draft,
                                                  tiktok_order_no: e.target.value,
                                                },
                                              }))
                                            }
                                          />
                                        </div>
                                        <div className="col-12 col-md-4">
                                          <label className="form-label">Package Fee</label>
                                          <input
                                            className="form-control"
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            value={draft.package_fee}
                                            onChange={(e) =>
                                              setDrafts((prev) => ({
                                                ...prev,
                                                [item.bill_no]: {
                                                  ...draft,
                                                  package_fee: e.target.value,
                                                },
                                              }))
                                            }
                                          />
                                          <div className="form-text">
                                            {selectedPackageItem
                                              ? `เลือกแล้ว: ${selectedPackageItem.product.name}`
                                              : "เลือกจากรายการแพ็กเกจด้านล่างเพื่อดึงต้นทุนจริง"}
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                          <label className="form-label">Shipping Fee</label>
                                          <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={draft.shipping_fee}
                                            onChange={(e) =>
                                              setDrafts((prev) => ({
                                                ...prev,
                                                [item.bill_no]: {
                                                  ...draft,
                                                  shipping_fee: e.target.value,
                                                },
                                              }))
                                            }
                                          />
                                        </div>
                                        <div className="col-12 col-md-4">
                                          <label className="form-label">วิธีกรอก Payout</label>
                                          <div className="btn-group w-100" role="group" aria-label="Payout edit mode">
                                            <button
                                              type="button"
                                              className={`btn btn-sm ${
                                                payoutMode === "TOTAL" ? "btn-primary" : "btn-outline-primary"
                                              }`}
                                              onClick={() =>
                                                setPayoutModes((prev) => ({
                                                  ...prev,
                                                  [item.bill_no]: "TOTAL",
                                                }))
                                              }
                                            >
                                              กรอกยอดรวม
                                            </button>
                                            <button
                                              type="button"
                                              className={`btn btn-sm ${
                                                payoutMode === "ITEMS" ? "btn-primary" : "btn-outline-primary"
                                              }`}
                                              onClick={() =>
                                                setPayoutModes((prev) => ({
                                                  ...prev,
                                                  [item.bill_no]: "ITEMS",
                                                }))
                                              }
                                            >
                                              กรอกรายชิ้น
                                            </button>
                                          </div>
                                          <div className="form-text">
                                            {payoutMode === "TOTAL"
                                              ? "ระบบจะกระจายยอดรวมไปยังแต่ละรายการอัตโนมัติแบบจำนวนเต็ม"
                                              : "ระบบจะรวมยอดจาก TikTok Net Payout ของแต่ละรายการให้อัตโนมัติ"}
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                          <label className="form-label">Total TikTok Payout</label>
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            className="form-control"
                                            value={
                                              payoutMode === "TOTAL"
                                                ? (totalPayoutRaw[totalRawKey] ?? String(Math.max(0, totalTikTokPayout)))
                                                : String(Math.max(0, totalTikTokPayout))
                                            }
                                            readOnly={payoutMode !== "TOTAL"}
                                            style={payoutMode !== "TOTAL" ? readonlyFieldStyle : undefined}
                                            onChange={(e) => {
                                              const raw = e.target.value;
                                              if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                                              setTotalPayoutRaw((prev) => ({ ...prev, [totalRawKey]: raw }));
                                              applyTotalTikTokPayout(item, draft, raw);
                                            }}
                                          />
                                          <div className="form-text">
                                            {payoutMode === "TOTAL"
                                              ? "กรอกยอดรวมแล้วระบบจะกระจายไปแต่ละสินค้า"
                                              : "ช่องนี้ล็อกอยู่ เพราะกำลังใช้โหมดกรอกรายชิ้น"}
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-6">
                                          <label className="form-label">Gross Sales Before Deductions</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={formatCurrency(item.total_amount)}
                                            readOnly
                                            style={readonlyFieldStyle}
                                          />
                                        </div>
                                        <div className="col-12 col-md-6">
                                          <label className="form-label">ส่วนลดจากหน้าแสกน</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={formatCurrency(discountAmount)}
                                            readOnly
                                            style={readonlyFieldStyle}
                                          />
                                        </div>
                                        <div className="col-12 col-md-6">
                                          <label className="form-label">ค่าธรรมเนียมจาก TikTok</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={formatCurrency(tiktokFee)}
                                            readOnly
                                            style={readonlyFieldStyle}
                                          />
                                        </div>
                                        <div className="col-12 col-md-6">
                                          <label className="form-label">Actual Sales Received</label>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={formatCurrency(actualSalesReceived)}
                                            readOnly
                                            style={readonlyFieldStyle}
                                          />
                                        </div>
                                      </div>

                                      <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div>
                                              <div className="fw-semibold">เลือกแพ็กเกจ</div>
                                              <div className="small text-muted">
                                              คลิกเพื่อดึงต้นทุนจริงมาใส่ Package Fee และบันทึก package ที่ใช้
                                              </div>
                                            </div>
                                          {packageItemsLoading && (
                                            <span className="small text-muted">กำลังโหลดรายการแพ็กเกจ...</span>
                                          )}
                                        </div>

                                        {packageItems.length === 0 ? (
                                          <div className="small text-muted">ยังไม่มีสินค้าในหมวดแพ็กเกจ</div>
                                        ) : (
                                          <div className="d-flex gap-2 overflow-auto pb-1">
                                            {packageItems.map((packageItem) => {
                                              const isSelected =
                                                draft.package_product_code === packageItem.product_code;
                                              const estimatedUnitCost = packageItem.product.estimated_unit_cost;
                                              const isOutOfStock = estimatedUnitCost == null;

                                              return (
                                                <button
                                                  key={packageItem.id}
                                                  type="button"
                                                  className={`btn rounded-3 d-flex flex-column align-items-start text-start flex-shrink-0 ${
                                                    isSelected ? "btn-primary" : "btn-outline-primary"
                                                  }`}
                                                  style={{ minWidth: 200 }}
                                                  disabled={isOutOfStock}
                                                  onClick={() =>
                                                    setDrafts((prev) => ({
                                                      ...prev,
                                                      [item.bill_no]: {
                                                        ...draft,
                                                        package_fee: String(estimatedUnitCost ?? 0),
                                                        package_product_code: packageItem.product_code,
                                                      },
                                                    }))
                                                  }
                                                >
                                                  <div className="d-flex align-items-baseline gap-2">
                                                    <span className="fw-semibold">{packageItem.product.name}</span>
                                                    <span className={isSelected ? "small text-white-50" : "small text-muted"}>
                                                      เหลือ {packageItem.product.available_qty}
                                                    </span>
                                                  </div>
                                                  <span className={isSelected ? "small text-white-50" : "small text-muted"}>
                                                    {packageItem.product.code}
                                                  </span>
                                                  <span className={isSelected ? "small text-white" : "small text-dark"}>
                                                    {isOutOfStock
                                                      ? "สต็อกไม่พอ"
                                                      : formatCurrency(estimatedUnitCost ?? 0)}
                                                  </span>
                                                </button>
                                              );
                                            })}

                                            <button
                                              type="button"
                                              className="btn btn-outline-secondary rounded-3 flex-shrink-0"
                                              style={{ minWidth: 140 }}
                                              onClick={() =>
                                                setDrafts((prev) => ({
                                                  ...prev,
                                                  [item.bill_no]: {
                                                    ...draft,
                                                    package_fee: "0",
                                                    package_product_code: "",
                                                  },
                                                }))
                                              }
                                            >
                                              ล้างแพ็กเกจ
                                            </button>
                                          </div>
                                        )}
                                      </div>

                                      <div className="d-flex justify-content-end mb-3">
                                        <button
                                          type="button"
                                          className="btn btn-primary"
                                          disabled={isSaving}
                                          onClick={() => void handleSave(item)}
                                        >
                                          {isSaving ? "Saving..." : "Save TikTok Values"}
                                        </button>
                                      </div>

                                      <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th style={{ width: 120 }}>Code</th>
                                              <th>Product</th>
                                              <th className="text-end" style={{ width: 80 }}>Qty</th>
                                              <th className="text-end" style={{ width: 140 }}>Unit Price</th>
                                              <th className="text-end" style={{ width: 180 }}>TikTok Net Payout</th>
                                              <th className="text-end" style={{ width: 140 }}>Line Total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {draftOrderItems.map((orderItem) => (
                                              <tr key={orderItem.id}>
                                                <td>{orderItem.product_code ?? "-"}</td>
                                                <td>{orderItem.product_name ?? "-"}</td>
                                                <td className="text-end">{orderItem.qty}</td>
                                                <td className="text-end">{formatCurrency(orderItem.unit_price)}</td>
                                                <td className="text-end">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control form-control-sm text-end"
                                  value={orderItem.draftNetPayout}
                                  readOnly={payoutMode === "TOTAL"}
                                  disabled={payoutMode === "TOTAL"}
                                  style={
                                    payoutMode === "TOTAL"
                                      ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" }
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                                        [item.bill_no]: {
                                                          ...draft,
                                                          order_items: {
                                                            ...draft.order_items,
                                                            [orderItem.id]: e.target.value,
                                                          },
                                                        },
                                                      }))
                                                    }
                                                  />
                                                </td>
                                                <td className="text-end">
                                                  {formatCurrency(orderItem.qty * orderItem.unit_price)}
                                                </td>
                                              </tr>
                                            ))}
                                            {item.order_items.length === 0 && (
                                              <tr>
                                                <td colSpan={6} className="text-center text-muted py-3">
                                                  No items found in this bill.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mt-3">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <span className={getTikTokStatusBadgeClass(item.tiktok_status)}>
                                            {getTikTokStatusLabel(item.tiktok_status)}
                                          </span>
                                          <span className="small text-muted">
                                            ใช้แบ่งว่าบิลนี้กรอกข้อมูล TikTok ครบแล้วหรือยัง
                                          </span>
                                        </div>

                                        <div className="d-flex gap-2">
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            disabled={isUpdatingStatus || item.tiktok_status === "PENDING"}
                                            onClick={() => void handleStatusUpdate(item, "PENDING")}
                                          >
                                            {isUpdatingStatus && item.tiktok_status !== "PENDING"
                                              ? "Updating..."
                                              : "ตั้งเป็นยังไม่ครบ"}
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-success btn-sm"
                                            disabled={isUpdatingStatus || item.tiktok_status === "COMPLETED"}
                                            onClick={() => void handleStatusUpdate(item, "COMPLETED")}
                                          >
                                            {isUpdatingStatus && item.tiktok_status !== "COMPLETED"
                                              ? "Updating..."
                                              : "เสร็จสิ้น"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
